import { z } from 'zod';

import { apiSuccess, apiList } from '@services/apiSchemas';

import { adminApiFetch } from './apiService';

// ---- Schemas (mirror app/controllers/api/admin/v1/contact_addresses_controller.rb) ----

// `index` / `create` / `update` — `render_data_item(@contact_address)` /
// `render_data_list(contact_addresses)` (AR `as_json` defaults). Loose because
// the full column set isn't fixed across migrations.
const ContactAddressSchema = z
  .object({
    id: z.string(),
    code: z.string().nullable(),
    receiver_name: z.string().nullable(),
    receiver_phone: z.string().nullable(),
    postal_code: z.string().nullable(),
    city: z.string().nullable(),
    district: z.string().nullable(),
    address: z.string().nullable(),
    country_code: z.string().nullable(),
  })
  .passthrough();

// `get_records` — explicit block in the controller.
const ChannelRefSchema = z.object({ id: z.string(), name: z.string() }).nullable();

const ContactAddressTableRowSchema = z
  .object({
    id: z.string(),
    code: z.string().nullable(),
    receiver_name: z.string().nullable(),
    receiver_phone: z.string().nullable(),
    postal_code: z.string().nullable(),
    city: z.string().nullable(),
    district: z.string().nullable(),
    address: z.string().nullable(),
    country_code: z.string().nullable(),
    merchant_name: z.string().nullable(),
    default_sales_channel_id: z.string().nullable(),
    default_sales_channel: ChannelRefSchema,
    created_at: z.string(),
    updated_at: z.string(),
  })
  .passthrough();

// `show` — explicit block in the controller.
const ContactAddressDetailSchema = z
  .object({
    id: z.string(),
    code: z.string().nullable(),
    receiver_name: z.string().nullable(),
    receiver_phone: z.string().nullable(),
    city: z.string().nullable(),
    district: z.string().nullable(),
    postal_code: z.string().nullable(),
    address: z.string().nullable(),
    country_code: z.string().nullable(),
    full_address: z.string().nullable(),
    default_sales_channel_id: z.string().nullable(),
    default_sales_channel: ChannelRefSchema,
    metadata: z.unknown().nullable(),
    created_at: z.string(),
    updated_at: z.string(),
    editable: z.boolean(),
    deletable: z.boolean(),
  })
  .passthrough();

const IdsListSchema = z.array(z.string());
const JobIdSchema = z.object({ job_id: z.string() }).passthrough();
const ImportJobSchema = z
  .object({ job_id: z.string(), import_id: z.string() })
  .passthrough();
const EmptyDataSchema = z.unknown();

// ---- Type exports ---------------------------------------------------------

export type ContactAddress = z.infer<typeof ContactAddressSchema>;
export type ContactAddressTableRow = z.infer<typeof ContactAddressTableRowSchema>;
export type ContactAddressDetail = z.infer<typeof ContactAddressDetailSchema>;

// ---- Input types ----------------------------------------------------------

interface ListParams {
  search?: string;
  limit?: number;
  /**
   * Admin 端的 contact_addresses controller 會以 session 鎖定 merchant，這個
   * 欄位實際被後端忽略；保留型別只是為了接受 FormReceiverInformation 等
   * 共用 caller 沿用 operator 端 signature 的傳法（同一個 component 兩邊都用）。
   */
  merchant_id?: string;
}

// ---- Service --------------------------------------------------------------

export const contactAddressService = {
  // 獲取地址列表 (Search)
  getList: async (params: ListParams = {}) => {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.limit) queryParams.append('limit', String(params.limit));

    const response = await adminApiFetch(`contact_addresses?${queryParams.toString()}`);
    return apiList(ContactAddressSchema).parse(await response.json());
  },

  fetchAddresses: async (params: ListParams = {}) => {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.limit) queryParams.append('limit', String(params.limit));

    const response = await adminApiFetch(`contact_addresses?${queryParams.toString()}`);
    return apiList(ContactAddressSchema).parse(await response.json());
  },

  getRecords: async (params: Record<string, unknown>) => {
    const response = await adminApiFetch('contact_addresses/get_records', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    return apiList(ContactAddressTableRowSchema).parse(await response.json());
  },

  getIds: async (params: Record<string, unknown>) => {
    const response = await adminApiFetch('contact_addresses/get_ids', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    return apiSuccess(IdsListSchema).parse(await response.json());
  },

  get: async (id: string) => {
    const response = await adminApiFetch(`contact_addresses/${id}`);
    return apiSuccess(ContactAddressDetailSchema).parse(await response.json());
  },

  create: async (data: Record<string, unknown>) => {
    const response = await adminApiFetch('contact_addresses', {
      method: 'POST',
      body: JSON.stringify({ contact_address: data }),
    });
    return apiSuccess(ContactAddressSchema).parse(await response.json());
  },

  update: async (id: string, data: Record<string, unknown>) => {
    const response = await adminApiFetch(`contact_addresses/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ contact_address: data }),
    });
    return apiSuccess(ContactAddressSchema).parse(await response.json());
  },

  delete: async (id: string) => {
    const response = await adminApiFetch(`contact_addresses/${id}`, {
      method: 'DELETE',
    });
    return apiSuccess(EmptyDataSchema).parse(await response.json());
  },

  // 匯出
  export: async (ids: string[]) => {
    const res = await adminApiFetch('contact_addresses/export', {
      method: 'POST',
      body: { ids },
    });
    return apiSuccess(JobIdSchema).parse(await res.json());
  },

  // 匯入
  import: async (formData: FormData) => {
    const res = await adminApiFetch('contact_addresses/import', {
      method: 'POST',
      body: formData,
    });
    return apiSuccess(ImportJobSchema).parse(await res.json());
  },

  // 下載匯入模板
  downloadImportTemplate: async (locale: string) => {
    return adminApiFetch('import_templates/download', {
      method: 'POST',
      body: JSON.stringify({ type: 'contact_address', locale }),
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
    });
  },
};
